import {Component, OnDestroy, OnInit} from '@angular/core';
import {combineLatest, Subject} from "rxjs";
import {NavigationEnd, Router, RouterEvent} from "@angular/router";
import {filter, startWith, takeUntil} from "rxjs/operators";
import {AppService, DealService, VehicleService} from "../../../../clearpath-module/services";
import {DealState} from "../../../../clearpath-module/store/state";
import {Vehicle} from "../../../../clearpath-module/models";

class Breadcrumb {
  label: string;
  url: string;
}

@Component({
  selector: 'app-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss']
})
export class BreadcrumbComponent implements OnInit, OnDestroy {
  private unsubscribe$: Subject<any> = new Subject();
  breadcrumbs: Breadcrumb[] = [];
  deal: DealState;
  vehicle: Vehicle;
  dealInfo = ''
  currentRoute: string;

  constructor(
    public appService: AppService,
    private router: Router,
    private dealService: DealService,
    private vehicleService: VehicleService,
  ) {
  }

  ngOnInit(): void {
    // Handle initial route and subsequent navigation events
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        startWith({url: this.router.url} as NavigationEnd), // Emit initial route
        takeUntil(this.unsubscribe$)
      )
      .subscribe((event: NavigationEnd) => {
        this.currentRoute = event.url;
        this.updateBreadcrumbs();
      });

    // Subscribe to deal and vehicle data changes
    combineLatest([
      this.dealService.selectDeal().pipe(startWith(null)),
      this.vehicleService.selectVehicle().pipe(startWith(null))
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([deal, vehicle]: [DealState, Vehicle]) => {
        this.deal = deal;
        this.vehicle = vehicle;
        this.updateDealInfo();
        // Update breadcrumbs if we have a current route
        if (this.currentRoute) {
          this.updateBreadcrumbs();
        }
      });
  }

  private updateDealInfo(): void {
    if (this.deal?.customer && this.vehicle?.stockNumber && 
        this.deal?.dealId == this.currentRoute?.split('/')[3]) {
      this.dealInfo = ': ' + 
        (this.vehicle?.stockNumber ? 'Stock ' + this.vehicle?.stockNumber + ' - ' : '') +
        (this.deal?.customer?.companyName ? this.deal?.customer?.companyName :
          this.deal?.customer?.firstName + ' ' + this.deal?.customer?.lastName);
    } else {
      this.dealInfo = '';
    }
  }

  private updateBreadcrumbs(): void {
    if (!this.currentRoute) return;
    
    let breadcrumbs: Breadcrumb[] = [];
    
    if (this.currentRoute.startsWith("/dashboard")) {
      breadcrumbs = this.buildDashboardBreadcrumbs(this.currentRoute);
    } else if (this.currentRoute.startsWith("/sales-manager")) {
      breadcrumbs = this.buildDeskingBreadcrumbs(this.currentRoute);
    } else if (this.currentRoute.startsWith("/clearpath") && !this.currentRoute.startsWith("/clearpath/new-deal")) {
      breadcrumbs = this.buildPresentationBreadcrumbs(this.currentRoute);
    } else if (this.currentRoute.startsWith("/settings") || this.currentRoute.startsWith("/user") || this.currentRoute.startsWith("/teams")) {
      breadcrumbs = this.buildAdminBreadcrumbs(this.currentRoute);
    } else if (this.currentRoute.startsWith("/clearpath/new-deal")) {
      breadcrumbs.push({label: 'Create Writeup', url: ''});
    }
    this.breadcrumbs = breadcrumbs;
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  buildDashboardBreadcrumbs(currentRoute: string): Breadcrumb[] {
    let breadcrumbs: Breadcrumb[] = [];
    if (currentRoute.startsWith("/dashboard")) {
      breadcrumbs.push({
        label: 'Dashboard',
        url: ''
      });
    }
    return breadcrumbs;
  }

  buildDeskingBreadcrumbs(currentRoute: string): Breadcrumb[] {
    let breadcrumbs: Breadcrumb[] = [];
    if (currentRoute.startsWith("/sales-manager/writeup/") ||
      currentRoute.startsWith("/sales-manager/activity/")) {
      let parts = currentRoute.split("/");
      breadcrumbs.push({
        label: 'Desking',
        url: '/sales-manager/list'
      });
      if (currentRoute.startsWith("/sales-manager/activity/")) {
        breadcrumbs.push({
          label: 'Deal' + this.dealInfo,
          url: '/sales-manager/writeup/' + parts[3]
        });
        breadcrumbs.push({
          label: 'Deal Activity',
          url: ''
        });
      } else {
        breadcrumbs.push({
          label: 'Deal' + this.dealInfo,
          url: ''
        });
      }
    } else {
      breadcrumbs.push({
        label: 'Desking',
        url: ''
      });
    }
    // console.log("Desking breadcrumbs: ", breadcrumbs);
    return breadcrumbs;
  }

  buildPresentationBreadcrumbs(currentRoute: string): Breadcrumb[] {
    let breadcrumbs: Breadcrumb[] = [];
    if (currentRoute.startsWith("/clearpath/deal/")) {
      breadcrumbs.push({
        label: 'Presentation',
        url: '/clearpath/list'
      });
      breadcrumbs.push({
        label: 'Deal' + this.dealInfo,
        url: ''
      });
    } else {
      breadcrumbs.push({
        label: 'Presentation',
        url: ''
      });
    }
    return breadcrumbs;
  }

  buildAdminBreadcrumbs(currentRoute: string): Breadcrumb[] {
    let breadcrumbs: Breadcrumb[] = [];
    if (currentRoute === "/settings") {
      breadcrumbs.push({label: 'Administration', url: ''});
    } else if (currentRoute === "/user") {
      breadcrumbs.push({label: 'Administration', url: '/settings'});
      breadcrumbs.push({label: 'Users', url: ''});
    } else if (currentRoute === "/teams") {
      breadcrumbs.push({label: 'Administration', url: '/settings'});
      breadcrumbs.push({label: 'Teams', url: ''});
    } else {
      breadcrumbs.push({label: 'Administration', url: '/settings'});
      let parts = currentRoute.split("/");
      console.log("parts: ", parts);
      switch (parts[2]) {
        case "dealer":
          breadcrumbs.push({label: 'Dealer Settings', url: ''});
          break;
        case "finance":
          breadcrumbs.push({label: 'Finance & Lease Settings', url: ''});
          break;
        case "lease-residuals":
          breadcrumbs.push({label: 'Lease Residuals', url: ''});
          break;
        case "insurance":
          breadcrumbs.push({label: 'Insurance Products', url: ''});
          break;
        case "incentives":
          breadcrumbs.push({label: 'Incentives', url: ''});
          break;
        case "menu":
          breadcrumbs.push({label: 'Menu & External Links', url: ''});
          break;
        case "form-server":
          breadcrumbs.push({label: 'Form Server Settings', url: ''});
          break;
        case "archive":
          breadcrumbs.push({label: 'Archive', url: ''});
          break;
        case "bug-reports":
          breadcrumbs.push({label: 'Bug Reports', url: ''});
          break;
        case "import-report":
          breadcrumbs.push({label: 'Import Reports', url: ''});
          break;
      }
    }
    return breadcrumbs;
  }
}
